import React, {Suspense} from 'react';
import {OdontoReviewBookingProps} from './types';
import {Box, Stack, Typography} from '@mui/material';
import {AppointmentBookingEnum, BookingProcessSuccessEnum} from 'main/analytics/gaTagsEnum';
import {translations} from 'presentation/translations-new';
import {ReviewCard} from 'presentation/modules/booking/shared/ui/components/ReviewCard';

import {SquareLabelTime} from 'presentation/modules/booking/shared/ui/components/SquareLabelTime';
import {ChangeFlowVariantEnum} from 'presentation/modules/booking/shared/ui/components/ChangeFlowDialog/constants';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {ProviderAddressField} from 'presentation/modules/booking/shared/ui/components/ProviderAddressField';
import {ProviderAddressSkeleton} from 'presentation/modules/booking/shared/ui/components/ProviderAddressField/ProviderAdressSkeleton';
import {formatText} from 'presentation/utils';
import {OdontoSummarySectionGroup} from '../../../../components/OdontoSummarySectionGroup';
import {useOdontoReviewBooking} from './hooks/useOdontoReviewBooking';
import {OdontoConfirmBookingButton} from './components/OdontoConfirmBookingButton';

const {appointmentSummary} = translations['pt-br'].privateFlow.bookingFlow;
const {professionalAndDate, specialty} = translations['pt-br'].commons;
const {appointmentCity} = translations['pt-br'].privateFlow.profile;

export function OdontoReviewBooking({handlePrevStep, stepLabels}: OdontoReviewBookingProps) {
	const {
		bookingData,
		day,
		month,
		formattedDateAndHour,
		isRebooking,
		handleChangeModals,
		iconName,
		isTablet,
		isLaptop,
		isMobile,
		activeBookingFlowStep,
	} = useOdontoReviewBooking();

	return (
		<Stack
			gap={2}
			m={0}
			data-tag-id={BookingProcessSuccessEnum.ACESSOU_ETAPA_REVISAO_AGENDAMENTO}>
			<Typography variant="subtitle2" color="text.secondary">
				{appointmentSummary}
			</Typography>
			<ReviewCard.Root>
				<ReviewCard.Header
					handleAction={handlePrevStep}
					subheader={professionalAndDate}
					actionDataTagId={AppointmentBookingEnum.CLICK_ALTERAR_INFORMACOES_AGENDAMENTO}
				/>
				<Stack flexDirection={'row'} gap={2} alignItems={'center'}>
					<SquareLabelTime day={day} month={month} />
					<Stack>
						<Typography variant="subtitle2" fontSize={'1.125rem'}>
							{formattedDateAndHour}
						</Typography>
						<ReviewCard.Content
							iconName="medical-services-filled"
							primaryText={bookingData.professionalName}
						/>
					</Stack>
				</Stack>
			</ReviewCard.Root>

			<ReviewCard.Root>
				<ReviewCard.Header
					handleAction={
						isRebooking
							? undefined
							: () => handleChangeModals(ChangeFlowVariantEnum.SPECIALTY)
					}
					subheader={specialty}
					actionDataTagId={AppointmentBookingEnum.CLICK_ALTERAR_INFORMACOES_AGENDAMENTO}
				/>
				<ReviewCard.Content iconName={iconName} primaryText={bookingData.specialtyName} />
			</ReviewCard.Root>

			<Box display={'grid'} gridTemplateColumns={isTablet ? '1fr' : '1fr 1fr'} gap={2}>
				<Suspense fallback={<ProviderAddressSkeleton />}>
					<ProviderAddressField flowType={FlowEnum.ODONTO} />
				</Suspense>

				<ReviewCard.Root>
					<ReviewCard.Header
						handleAction={
							isRebooking
								? undefined
								: () => handleChangeModals(ChangeFlowVariantEnum.LOCATION)
						}
						subheader={appointmentCity}
						actionDataTagId={
							AppointmentBookingEnum.CLICK_ALTERAR_INFORMACOES_AGENDAMENTO
						}
					/>
					<ReviewCard.Content
						iconName={'my-location-filled'}
						primaryText={formatText(bookingData.cityName)}
						secondaryText={formatText(bookingData.ufName)}
					/>
				</ReviewCard.Root>
			</Box>

			<Stack gap={2}>
				{isLaptop ? (
					<OdontoSummarySectionGroup
						noBorderRadius={isMobile}
						showTitle={false}
						activeStep={activeBookingFlowStep}
						stepLabels={stepLabels}
						handleChangeModals={handleChangeModals}
					/>
				) : null}

				<OdontoConfirmBookingButton handleBack={handlePrevStep} />
			</Stack>
		</Stack>
	);
}
